import React, {useEffect, useMemo, useState} from "react";
import {useAtom} from "jotai";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, HeadsetIcon, PhoneCallIcon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {StartCallAtom} from "../../../pages/project/atoms";
import {useAmiActiveExtensions} from "../../../hooks/useAmiActiveExtensions";
import {AmiRepository} from "../../../repositories/AmiRepository";
import {CallOrb} from "../../../components/voice-orb";
import {getDefaultSipAudioConfig, isSipConfigured, useSipAudioClient} from "../../../utils/sipAudio";
import useCallConfirm from "../../../hooks/useCallConfirm";

const MODE_META = {
  web: {icon: HeadsetIcon, title: "Start a Web Call", cta: "Start Call"},
  phone: {icon: PhoneCallIcon, title: "Start a Phone Call", cta: "Call"},
};

// Renders the dialplan-picker step for the Start node's "Web Call" and "Phone Call" menu items
// (see StartNode.js), then hands off to the shared CallOrb console for a web call, or originates
// an outbound call through the AMI API for a phone call. Mounted once alongside ChatDrawer so it
// has access to `project` (StartNode itself only gets id/selected from react-flow).
export const StartCallModal = ({project}) => {
  const [startCall, setStartCall] = useAtom(StartCallAtom);
  const {open, mode} = startCall;
  const meta = MODE_META[mode] || MODE_META.web;

  const sipConfig = useMemo(() => getDefaultSipAudioConfig(), []);
  const sipConfigured = isSipConfigured(sipConfig);
  const sip = useSipAudioClient(sipConfig);

  const {extensions, loading: extensionsLoading} = useAmiActiveExtensions(project?.id, open);

  const [selectedExtension, setSelectedExtension] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [calling, setCalling] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const confirmCall = useCallConfirm("Start this call?");

  useEffect(() => {
    if (!open) {
      setSelectedExtension("");
      setPhoneNumber("");
      setCalling(false);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && !selectedExtension && extensions.length === 1) {
      setSelectedExtension(extensions[0].extension);
    }
  }, [open, extensions, selectedExtension]);

  const handleClose = () => setStartCall({open: false, mode: null});

  const handleWebCallStart = async () => {
    if (!selectedExtension) return;
    const confirmed = await confirmCall({title: `Start a web call to ${selectedExtension}?`});
    if (confirmed) setCalling(true);
  };

  const handleWebCallClose = () => {
    setCalling(false);
    handleClose();
  };

  const handlePhoneCall = async () => {
    if (!selectedExtension || !phoneNumber.trim()) return;
    const confirmed = await confirmCall({
      title: `Call ${phoneNumber.trim()}?`,
      subtitle: `Via dialplan ${selectedExtension}`,
    });
    if (!confirmed) return;

    setSubmitting(true);
    const callerId = project?.configuration?.outbound_call || project?.configuration?.redirect_vdn || undefined;
    AmiRepository.originateCall(project?.id, {
      to_number: phoneNumber.trim(),
      extension: selectedExtension,
      caller_id: callerId,
      timeout: 30000,
      async: true,
      variables: {},
    })
      .then(() => {
        toast.success(`Calling ${phoneNumber.trim()}…`);
        handleClose();
      })
      .catch((error) => {
        toast.error(error?.response?.data?.error || error?.message || "Failed to originate call");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <>
      {/* Mounted as soon as this component exists so sip.js has an <audio> element ready
          before CallOrb auto-dials. */}
      <audio ref={sip.remoteAudioRef} hidden autoPlay playsInline />

      {mode === "web" && calling && (
        <CallOrb sip={sip} onClose={handleWebCallClose} extension={selectedExtension} title={`Web Call — ${selectedExtension}`} />
      )}

      <Dialog
        open={Boolean(open && mode && !(mode === "web" && calling))}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{sx: {borderRadius: 4, overflow: "hidden"}}}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{px: 3, pt: 2.5, pb: 1.5}}>
          <Stack direction="row" alignItems="center" gap={1.25}>
            <Box sx={{width: 38, height: 38, borderRadius: "12px", display: "grid", placeItems: "center", bgcolor: "rgba(98,88,232,0.1)", color: "#6258E8"}}>
              <HugeiconsIcon icon={meta.icon} size={20} strokeWidth={1.8}/>
            </Box>
            <Typography fontWeight={700} fontSize="1.05rem">{meta.title}</Typography>
          </Stack>
          <IconButton size="small" onClick={handleClose}><HugeiconsIcon icon={Cancel01Icon} size={18}/></IconButton>
        </Stack>

        <DialogContent sx={{pt: 0.5}}>
          <Stack gap={2}>
            {mode === "web" && !sipConfigured && (
              <Typography fontSize="0.82rem" color="error.main">
                Web calling isn't configured (REACT_APP_SIP_*) — ask your team to switch it on, or use Phone Call instead.
              </Typography>
            )}

            <Stack gap={0.75}>
              <Typography fontWeight={600} fontSize="0.85rem">Dialplan</Typography>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={selectedExtension}
                onChange={(e) => setSelectedExtension(e.target.value)}
                disabled={extensionsLoading || extensions.length === 0}
              >
                <MenuItem value="" disabled>
                  {extensionsLoading ? "Loading active dialplans…" : extensions.length === 0 ? "No active dialplans" : "Select a dialplan"}
                </MenuItem>
                {extensions.map((ext) => (
                  <MenuItem key={ext.id || ext.extension} value={ext.extension}>
                    {ext.extension}{ext.description ? ` — ${ext.description}` : ""}
                  </MenuItem>
                ))}
              </Select>
              {!extensionsLoading && extensions.length === 0 && (
                <Typography fontSize="0.78rem" color="text.secondary">
                  No enabled dialplan extensions for this project yet — enable one under Dialplan settings first.
                </Typography>
              )}
            </Stack>

            {mode === "phone" && (
              <Stack gap={0.75}>
                <Typography fontWeight={600} fontSize="0.85rem">Phone number</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter phone number to call"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
              </Stack>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{px: 3, pb: 3, pt: 1}}>
          <Button onClick={handleClose} color="secondary" variant="ghost">Cancel</Button>
          {mode === "web" ? (
            <Button
              variant="contained"
              color="success"
              disabled={!selectedExtension || !sipConfigured}
              startIcon={<HugeiconsIcon icon={HeadsetIcon} size={18}/>}
              sx={{borderRadius: 2.5, px: 2.5}}
              onClick={handleWebCallStart}
            >
              {meta.cta}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              disabled={!selectedExtension || !phoneNumber.trim() || submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <HugeiconsIcon icon={PhoneCallIcon} size={18}/>}
              sx={{borderRadius: 2.5, px: 2.5}}
              onClick={handlePhoneCall}
            >
              {meta.cta}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
